import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, Modal, Badge } from "@/components/UI";
import { CalendarDays, ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";
import { Link } from "react-router-dom";

const WD = ["Pzt", "Sal", "Çrş", "Prş", "Cum", "Cmt", "Paz"];

export default function GMPCalendar() {
  const [ym, setYm] = useState(() => new Date());
  const [days, setDays] = useState([]);
  const [detail, setDetail] = useState(null);
  const y = ym.getFullYear();
  const m = ym.getMonth() + 1;

  useEffect(() => {
    api.get("/gmp/calendar", { year: y, month: m }).then(r => setDays(r.days || [])).catch(() => setDays([]));
  }, [y, m]);

  const openDay = async (d) => {
    try {
      const r = await api.get("/gmp/day-detail", { date: d.date });
      setDetail(r);
    } catch (e) { setDetail({ date: d.date, cleaning: [], temp_humidity: [], events: [], error: true }); }
  };

  const firstDow = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const cells = Array(firstDow).fill(null).concat(days);

  const shift = (dir) => setYm(new Date(y, m - 1 + dir, 1));
  const monthStr = ym.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  // G.T. ve S.N.T. durumu — bölge sayısına göre yapıldı/eksik/kısmi
  const cleaningState = (d) => {
    if (!d || d.cleaning_expected === 0) return { key: "none", label: "—", text: "text-slate-300" };
    if (d.cleaning_not_done) return { key: "incident", label: "✗", text: "text-red-600 font-bold" };
    if (d.cleaning_zones_done >= d.cleaning_expected) return { key: "ok", label: "✓", text: "text-emerald-600 font-bold" };
    if (d.cleaning_zones_done > 0) return { key: "partial", label: "◐", text: "text-yellow-600 font-bold" };
    return { key: "missing", label: "×", text: "text-slate-400" };
  };
  const tempState = (d) => {
    if (!d || d.temp_expected === 0) return { key: "none", label: "—", text: "text-slate-300" };
    if (d.temp_out_of_range) return { key: "incident", label: "✗", text: "text-red-600 font-bold" };
    if (d.temp_zones_recorded >= d.temp_expected) return { key: "ok", label: "✓", text: "text-emerald-600 font-bold" };
    if (d.temp_zones_recorded > 0) return { key: "partial", label: "◐", text: "text-yellow-600 font-bold" };
    return { key: "missing", label: "×", text: "text-slate-400" };
  };

  return (
    <Card className="p-4" data-testid="gmp-calendar">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2 text-slate-800 dark:text-slate-100"><CalendarDays size={14} className="text-emerald-600"/> GMP Takvim — <span className="capitalize">{monthStr}</span></h3>
        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)} className="p-1 rounded hover:bg-slate-100" data-testid="gmp-cal-prev"><ChevronLeft size={14}/></button>
          <button onClick={() => shift(1)} className="p-1 rounded hover:bg-slate-100" data-testid="gmp-cal-next"><ChevronRight size={14}/></button>
          <Link to="/gmp" className="ml-2 text-xs text-emerald-700 hover:underline flex items-center gap-1"><ExternalLink size={11}/> GMP</Link>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] text-slate-500 uppercase font-semibold">
        {WD.map(w => <div key={w} className="text-center">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i}></div>;
          const c = cleaningState(d);
          const t = tempState(d);
          return (
            <div key={i} onClick={() => openDay(d)} className={`min-h-[68px] border rounded p-1 flex flex-col items-stretch transition cursor-pointer ${d.date === todayStr ? "border-emerald-500 border-2 bg-emerald-50/30" : "border-slate-200"} hover:bg-slate-100 dark:hover:bg-slate-800/50`}
                 title={`${d.date} · G.T. ${d.cleaning_zones_done}/${d.cleaning_expected}${d.cleaning_not_done ? " (uygunsuzluk)" : ""} · S.N.T. ${d.temp_zones_recorded}/${d.temp_expected}${d.temp_out_of_range ? " (sınır dışı)" : ""} · Detay için tıklayın`}
                 data-testid={`gmp-day-${d.day}`}>
              <div className="font-mono text-[11px] text-slate-700 text-right leading-none">{d.day}</div>
              <div className="mt-1 flex flex-col gap-0.5 text-[9px] leading-tight">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-semibold">G.T.</span>
                  <span className={c.text}>{c.label}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-semibold">S.N.T.</span>
                  <span className={t.text}>{t.label}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-slate-600">
        <span><span className="text-emerald-600 font-bold">✓</span> Tamamlandı</span>
        <span><span className="text-yellow-600 font-bold">◐</span> Kısmi</span>
        <span><span className="text-red-600 font-bold">✗</span> Uygunsuzluk</span>
        <span><span className="text-slate-400 font-bold">×</span> Kayıt Yok</span>
        <span className="ml-auto text-slate-400 italic">G.T. = Günlük Temizlik · S.N.T. = Sıcaklık/Nem Takibi</span>
      </div>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={`GMP Detay — ${detail?.date || ""}`} size="lg">
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-500/30 rounded p-2">
                <div className="text-[10px] uppercase text-emerald-700 dark:text-emerald-400 font-semibold">G.T. Yapılan</div>
                <div className="font-mono text-base font-bold">{detail.counts?.cleaning_zones_done}/{detail.counts?.cleaning_expected}</div>
              </div>
              <div className={`${detail.counts?.cleaning_not_done_any ? "bg-red-50 dark:bg-red-900/20 border-red-200" : "bg-slate-50 dark:bg-slate-800/40 border-slate-200"} border rounded p-2`}>
                <div className="text-[10px] uppercase font-semibold text-slate-600 dark:text-slate-400">G.T. Uygunsuzluk</div>
                <div className="font-mono text-base font-bold">{detail.counts?.cleaning_not_done_any ? "Var ✗" : "Yok"}</div>
              </div>
              <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-500/30 rounded p-2">
                <div className="text-[10px] uppercase text-cyan-700 dark:text-cyan-400 font-semibold">S.N.T. Kayıtlı</div>
                <div className="font-mono text-base font-bold">{detail.counts?.temp_zones_recorded}/{detail.counts?.temp_expected}</div>
              </div>
              <div className={`${detail.counts?.temp_out_of_range_any ? "bg-red-50 dark:bg-red-900/20 border-red-200" : "bg-slate-50 dark:bg-slate-800/40 border-slate-200"} border rounded p-2`}>
                <div className="text-[10px] uppercase font-semibold text-slate-600 dark:text-slate-400">S.N.T. Sınır Dışı</div>
                <div className="font-mono text-base font-bold">{detail.counts?.temp_out_of_range_any ? "Var ✗" : "Yok"}</div>
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-emerald-700 dark:text-emerald-400 mb-1">G.T. — Günlük Temizlik Kayıtları</div>
              {detail.cleaning?.length === 0 && <div className="text-xs text-slate-400 italic py-2">Bu gün için temizlik kaydı yok.</div>}
              {detail.cleaning?.length > 0 && (
                <table className="w-full text-xs border border-slate-200 dark:border-slate-700 rounded overflow-hidden">
                  <thead className="bg-slate-100 dark:bg-slate-800"><tr><th className="text-left px-2 py-1">Saat</th><th className="text-left px-2 py-1">Bölge</th><th className="px-2 py-1">Durum</th><th className="text-left px-2 py-1">Not</th><th className="text-left px-2 py-1">Kayıt Eden</th></tr></thead>
                  <tbody>{detail.cleaning.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-700/50">
                      <td className="px-2 py-1 font-mono">{r.time || "—"}</td>
                      <td className="px-2 py-1">{r.zone}</td>
                      <td className="px-2 py-1 text-center"><Badge level={r.status === "done" ? "ok" : "missing"}>{r.status === "done" ? "✓ Yapıldı" : "✗ Yapılmadı"}</Badge></td>
                      <td className="px-2 py-1 text-slate-600 dark:text-slate-400">{r.notes || "—"}</td>
                      <td className="px-2 py-1">{r.created_by_name || "—"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-cyan-700 dark:text-cyan-400 mb-1">S.N.T. — Sıcaklık ve Nem Ölçümleri</div>
              {detail.temp_humidity?.length === 0 && <div className="text-xs text-slate-400 italic py-2">Bu gün için ölçüm kaydı yok.</div>}
              {detail.temp_humidity?.length > 0 && (
                <table className="w-full text-xs border border-slate-200 dark:border-slate-700 rounded overflow-hidden">
                  <thead className="bg-slate-100 dark:bg-slate-800"><tr><th className="text-left px-2 py-1">Saat</th><th className="text-left px-2 py-1">Bölge</th><th className="px-2 py-1">Sıcaklık</th><th className="px-2 py-1">Nem</th><th className="text-left px-2 py-1">Not</th><th className="text-left px-2 py-1">Kayıt Eden</th></tr></thead>
                  <tbody>{detail.temp_humidity.map((r, i) => {
                    const tOut = r.temperature_c < 15 || r.temperature_c > 30;
                    const hOut = r.humidity_pct < 20 || r.humidity_pct > 70;
                    return (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-700/50">
                      <td className="px-2 py-1 font-mono">{r.time || "—"}</td>
                      <td className="px-2 py-1">{r.zone}</td>
                      <td className={`px-2 py-1 text-center font-mono ${tOut ? "text-red-600 font-bold" : ""}`}>{r.temperature_c?.toFixed(1)}°C{tOut && " ⚠"}</td>
                      <td className={`px-2 py-1 text-center font-mono ${hOut ? "text-red-600 font-bold" : ""}`}>%{r.humidity_pct?.toFixed(1)}{hOut && " ⚠"}</td>
                      <td className="px-2 py-1 text-slate-600 dark:text-slate-400">{r.notes || "—"}</td>
                      <td className="px-2 py-1">{r.created_by_name || "—"}</td>
                    </tr>
                  );})}</tbody>
                </table>
              )}
            </div>

            {detail.events?.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase text-violet-700 dark:text-violet-400 mb-1">Genel Takip Olayları</div>
                <ul className="text-xs list-disc list-inside text-slate-700 dark:text-slate-300">
                  {detail.events.map((e, i) => <li key={i}>{e.kind} — {JSON.stringify(e.payload || {}).slice(0, 100)}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
}
